<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header Section -->
      <div>
        <div class="flex items-center justify-between element-island">
          <div class="flex items-center space-x-4">
            <div>
              <RouterLink
                :to="{ name: 'ReportsIndex', params: { projectId: props.projectId } }"
                class="btn btn--secondary-blue flex items-center space-x-2"
              >
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
</svg>

                <span>Reports</span>
              </RouterLink>
            </div>

            <div class="flex flex-col">
              <h1 class="text-3xl font-bold text-gray-900">
                {{ reportId ? 'Edit Report' : 'Create Report' }}
              </h1>

              <p class="mt-2 text-sm text-gray-600">
                Configure report settings and sections
              </p>
            </div>
          </div>

          <div class="flex items-center space-x-2">
            <RouterLink
              v-if="report?.save_export"
              :to="{ name: 'ReportShow', params: { projectId: props.projectId, reportId: props.reportId } }"
              class="btn btn--secondary-blue flex items-center space-x-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>

              <span>
                View Saved Reports
              </span>
            </RouterLink>

            <button
              v-if="reportId"
              type="button"
              class="btn btn--secondary-blue flex items-center space-x-2"
              @click="showExportReportModal = true"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <span>
                Generate Report
              </span>
            </button>

            <button
              type="button"
              class="btn btn--primary-blue flex items-center space-x-2"
              :disabled="saveReportAction.is(States.LOADING) || updateReportAction.is(States.LOADING)"
              @click="saveReport"
            >
              <AppSpinner
                v-if="saveReportAction.is(States.LOADING) || updateReportAction.is(States.LOADING)"
                class="w-4 h-4"
              />

              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" v-else-if="!reportId"
               
                class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 v-else
               
                class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>

              <span>{{ reportId ? 'Update' : 'Create' }} Report</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8">
        <!-- Form Header -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="flex items-center space-x-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>

            <div class="flex flex-col">
              <h3 class="text-lg font-semibold text-gray-900">
                {{ props.reportId ? report?.title : 'Report Configuration' }}
              </h3>

              <p
                v-if="props.reportId"
                class="text-sm text-gray-600"
              >
                Report Configuration
              </p>
            </div>
          </div>
        </div>

        <!-- Form Content -->
        <div class="p-6">
          <form
            class="space-y-6"
            @submit.prevent="saveReport"
          >
            <!-- Basic Information -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Report Title</label>

                <AppInput
                  v-model="reportForm.form.title"
                  type="text"
                  name="title"
                  class="form-input"
                  :error="reportForm.getErrors('title')"
                  placeholder="Enter report title"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Export File Name</label>

                <AppInput
                  v-model="reportForm.form.export_file_name"
                  type="text"
                  name="export_file_name"
                  class="form-input"
                  :error="reportForm.getErrors('export_file_name')"
                  placeholder="report_filename"
                />

                <p class="text-xs text-gray-500 mt-1">
                  Only letters, numbers, hyphens, and underscores allowed

                  Add :project to insert the project name or :month to insert the month that the report covers.
                </p>
              </div>
            </div>

            <!-- Auto Generation Settings -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Auto Generate Day</label>

                <AppInput
                  v-model="reportForm.form.auto_generate_day"
                  type="number"
                  name="auto_generate_day"
                  class="form-input"
                  :error="reportForm.getErrors('auto_generate_day')"
                  placeholder="Day of month (1-31)"
                  min="1"
                  max="31"
                />

                <p class="text-xs text-gray-500 mt-1">
                  Leave empty to disable auto-generation
                </p>
              </div>

              <div class="flex items-center space-x-4">
                <label class="flex items-center">
                  <AppInput
                    v-model="reportForm.form.document_control_version"
                    type="checkbox"
                    name="document_control_version"
                    class="form-checkbox"
                  />

                  <span class="ml-2 text-sm text-gray-700">Document Control Version</span>
                </label>

                <label class="flex items-center">
                  <AppInput
                    v-model="reportForm.form.show_header"
                    type="checkbox"
                    name="show_header"
                    class="form-checkbox"
                  />

                  <span class="ml-2 text-sm text-gray-700">Show Header</span>
                </label>

                <label class="flex items-center">
                  <AppInput
                    v-model="reportForm.form.show_footer"
                    type="checkbox"
                    name="show_footer"
                    class="form-checkbox"
                  />

                  <span class="ml-2 text-sm text-gray-700">Show Footer</span>
                </label>

                <label class="flex items-center">
                  <AppInput
                    v-model="reportForm.form.save_export"
                    type="checkbox"
                    name="save_export"
                    class="form-checkbox"
                    :disabled="reportForm.form.document_control_version"
                  />

                  <span class="ml-2 text-sm text-gray-700">Save Report</span>
                </label>
              </div>
            </div>

            <!-- Email Message -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Email Message</label>

              <AppTextArea
                v-model="reportForm.form.email_message"
                name="email_message"
                rows="4"
                class="form-input resize-none"
                placeholder="Enter email message to be sent with the report..."
              />
            </div>

            <!-- Issue To Section -->
            <div>
              <div class="flex items-center justify-between mb-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Issue To</label>

                  <p
                    v-if="reportForm.hasErrors('issue_to')"
                    class="text-red-600 text-sm"
                  >
                    {{ reportForm.getErrors('issue_to') }}
                  </p>

                  <p class="text-xs text-gray-500 mt-1">
                    Add users or email addresses to receive the report when it is auto generated.

                    You may also manually issue reports when needed.
                  </p>
                </div>


                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click="addIssueTo"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                  <span>Add Recipient</span>
                </button>
              </div>

              <div
                v-if="reportForm.form.issue_to.length === 0"
                class="text-center py-8 text-gray-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12 mx-auto mb-2 text-gray-300">
    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
</svg>

                <p>No recipients added yet</p>
              </div>

              <div
                v-else
                class="space-y-3"
              >
                <div
                  v-for="(recipient, index) in reportForm.form.issue_to"
                  :key="index"
                  class="flex items-end space-x-3 p-3 border border-gray-200 rounded-lg"
                >
                  <div class="group w-full">
                    <div class="flex justify-between">
                      <label class="block text-xs font-medium text-gray-600 mb-1">System User (Optional)</label>

                      <button
                        v-if="recipient.user"
                        type="button"
                        @click="clearUser(index)"
                      >
                        <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="text-red-600 w-5 h-5 invisible group-hover:visible">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
                      </button>
                    </div>

                    <AppTypeAhead
                      v-model="recipient.userNameTypeAhead"
                      :name="`issue_to.${index}.user`"
                      class="form-input w-full"
                      :suggestion-callback="() => getSuggestions(index)"
                      :initial-suggestions="() => getSuggestions(index)"
                      placeholder="Search for user..."
                      @value-selected="(userIndex) => selectUser(index, userIndex)"
                    />
                  </div>

                  <div class="w-full">
                    <label class="block text-xs font-medium text-gray-600 mb-1">Email</label>

                    <div class="flex space-x-3 w-full">
                      <div class="flex-1">
                        <AppInput
                          v-model="recipient.email"
                          type="email"
                          :name="`issue_to.${index}.email`"
                          class="form-input w-full"
                          :error="reportForm.getErrors(`issue_to.${index}.email`)"
                          placeholder="name@example.com"
                          :disabled="reportForm.form.issue_to[index].user"
                        />
                      </div>

                      <button
                        type="button"
                        class="btn-icon btn-icon-red flex-shrink-0"
                        @click="removeIssueTo(index)"
                      >
                        <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Report Sections -->
            <div>
              <div class="flex items-center justify-between mb-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Report Sections</label>

                  <p
                    v-if="reportForm.hasErrors('report_section_configs')"
                    class="text-red-600 text-sm"
                  >
                    {{ reportForm.getErrors('report_section_configs') }}
                  </p>

                  <p class="text-xs text-gray-500 mt-1">
                    Build your report by adding sections.
                  </p>
                </div>

                <button
                  v-if="$props.reportId"
                  type="button"
                  class="btn btn--tertiary-blue flex items-center space-x-2 ml-auto"
                  @click="revertSections"
                >
                  <span>Revert Changes</span>
                </button>

                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click="addReportSection"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                  <span>Add Section</span>
                </button>
              </div>

              <div
                v-if="reportForm.form.report_section_configs?.length === 0"
                class="text-center py-8 text-gray-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12 mx-auto mb-2 text-gray-300">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                <p>No report sections added.</p>
              </div>

              <div
                v-else
                class="space-y-3"
              >
                <Sortable
                  :list="reportForm.form.report_section_configs"
                  item-key="id"
                  class="space-y-3"
                  handle=".drag-handle"
                  @end="updateSectionOrder"
                >
                  <template #item="{ element, index }">
                    <div class="w-full flex items-center p-3 border border-gray-200 rounded-lg bg-white">
                      <button
                        type="button"
                        class="drag-handle btn-icon btn-icon-default mr-3 cursor-grab active:cursor-grabbing"
                      >
                        <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 8h16M4 16h16"
    />
</svg>
                      </button>

                      <div class="flex flex-col">
                        <div class="flex items-center space-x-2">
                          <AppSelect
                            v-model="element.report_section_id"
                            :name="`report_section_configs.${index}.report_section_id`"
                            class="form-input w-fit pr-8"
                            :options="availableReportSections"
                            placeholder="Select report section"
                            :error="reportForm.getErrors(`report_section_configs.${index}.report_section_id`)"
                            @change="updateSectionId($event.target.value, index)"
                          />

                          <div
                            v-if="!element.id"
                            class="bg-ccm-blue-500 text-white px-2 py-1 rounded-md text-xs"
                          >
                            New
                          </div>
                        </div>

                        <div>
                          {{ reportSections.find((reportSection) => reportSection.id === element.report_section_id)?.description }}
                        </div>
                      </div>

                      <button
                        type="button"
                        class="btn-icon btn-icon-red flex-shrink-0 ml-auto"
                        @click="removeReportSection(index)"
                      >
                        <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                      </button>
                    </div>
                  </template>
                </Sortable>
              </div>
            </div>

            <div class="flex justify-end items-center space-x-2">
              <button
                type="button"
                class="btn btn--primary-blue flex items-center space-x-2"
                :disabled="saveReportAction.is(States.LOADING) || updateReportAction.is(States.LOADING)"
                @click="saveReport"
              >
                <AppSpinner
                  v-if="saveReportAction.is(States.LOADING) || updateReportAction.is(States.LOADING)"
                  class="w-4 h-4"
                />

                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" v-else-if="!reportId"
                 
                  class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 v-else
                 
                  class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>

                <span>{{ reportId ? 'Update' : 'Create' }} Report</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <GenerateExportModal
      v-if="showExportReportModal && report"
      :report="report"
      @close="showExportReportModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import GenerateExportModal from '@/components/Reports/ReportShow/GenerateExportModal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { useProjectsStore } from '@/store/projects';
import { useReportsStore } from '@/store/reports';
import { useReportSectionsStore } from '@/store/reportSections';
import { useUserStore } from '@/store/user';
import { Sortable } from 'sortablejs-vue3';
import Swal from 'sweetalert2';
import { computed, onMounted, ref, toRaw, watch } from 'vue';
import { useRouter } from 'vue-router';
import draggable from 'vuedraggable';

const props = defineProps<{
  reportId?: string;
  projectId: string;
}>();

const router = useRouter();
const reportsStore = useReportsStore();
const reportSectionsStore = useReportSectionsStore();
const userStore = useUserStore();
const projectsStore = useProjectsStore();
// Fetch data
const fetchReportSectionsAction = useStoreApiAction(reportSectionsStore.index);
const fetchReportAction = useStoreApiAction(reportsStore.show);
const saveReportAction = useStoreApiAction(reportsStore.store);
const updateReportAction = useStoreApiAction(reportsStore.update);
// Modal state management
const showExportReportModal = ref(false);

// Load report sections
watch(() => props.projectId, (newValue) => {
  if(newValue) {
    fetchReportSectionsAction.request(newValue).catch(console.error);
  }
}, { immediate: true });

const report = computed(() => {
  return reportsStore.models.with('report_section_configs', (query) => query.orderBy('sort_order')).find(
    parseInt(props.reportId),
  );
});

// Form data
const reportForm = useFormData({
  title: '',
  export_file_name: '',
  auto_generate_day: undefined as number | undefined,
  issue_to: [] as {
    email: string;
    user?: number;
    userNameTypeAhead?: string;
    user_details?: { id: number; name: string; email: string };
  }[],

  email_message: '',
  document_control_version: false,
  save_export: false,
  show_header: true,
  show_footer: true,
  report_section_configs: [] as { report_section_id: number; id?: number }[],
});

// if report section id changes, then remove the id to indicate its a new section
const updateSectionId = (value: string, index: number) => {
  // if it updates to the original report section, then re-set the id
  // if(parseInt(value) === reportForm.form.report_section_configs[index].report_section_id) {
  //   reportForm.form.report_section_configs[index].id = report.value?.report_section_configs[index]?.id;
  // }

  if(parseInt(value) !== reportForm.form.report_section_configs[index].report_section_id) {
    reportForm.form.report_section_configs[index].id = undefined;
  }
};

watch(() => reportForm.form.document_control_version, (newValue) => {
  if(newValue) {
    reportForm.form.save_export = true;
  }
});

watch(() => props.reportId, (newValue) => {
  if(newValue) {
    fetchReportAction.request(newValue).then((data) => {
      // reportForm.setData(data.data);

      // Populate userNameTypeAhead for existing recipients
      // if(data.data.issue_to) {
      //   data.data.issue_to.forEach((recipient: any, index: number) => {
      //     if(recipient.user) {
      //       const user = userStore.models.find(recipient.user);

      //       if(user) {
      //         reportForm.form.issue_to[index].userNameTypeAhead = `${user.name} (${user.email})`;
      //       }
      //     }
      //   });
      // }
    }).catch(console.error);
  }
}, { immediate: true });

watch(report, (newValue) => {
  if(newValue) {
    reportForm.setData(newValue, ['report_section_configs', 'issue_to']);

    reportForm.form.report_section_configs = structuredClone(toRaw(newValue.report_section_configs));

    reportForm.form.issue_to = structuredClone(toRaw(newValue.issue_to));

    reportForm.form.issue_to.map((recipient) => {
      if(recipient.user && recipient.user_details) {
        recipient.userNameTypeAhead = recipient.user_details.name;
      }

      return recipient;
    });
  }
}, { immediate: true });

// Computed properties
const reportSections = computed(() => {
  return reportSectionsStore.models.all();
});

const availableReportSections = computed(() => {
  return reportSections.value.map((section) => ({
    label: section.name,
    value: section.id,
  }));
});

const availableUsers = computed(() => {
  return userStore.models.all().map((user) => ({
    label: `${user.name} (${user.email})`,
    value: user.id,
  }));
});

// User search functionality
const suggestedUsers = ref<{ id: number; name: string; email: string }[]>([]);

const getSuggestions = async (index: number) => {
  const recipient = reportForm.form.issue_to[index];

  if(!recipient.userNameTypeAhead) return [];

  suggestedUsers.value = await projectsStore
    .searchUsers(props.projectId, { name: recipient.userNameTypeAhead });

  return suggestedUsers.value.map((value) => `${value.name} (${value.email})`);
};

const selectUser = (index: number, userIndex: number) => {
  if(suggestedUsers.value[userIndex]) {
    const user = suggestedUsers.value[userIndex];

    reportForm.form.issue_to[index].user = user.id;
    reportForm.form.issue_to[index].email = user.email;
    reportForm.form.issue_to[index].userNameTypeAhead = user.name;
  }
};

const clearUser = (index: number) => {
  reportForm.form.issue_to[index].user = undefined;
  reportForm.form.issue_to[index].userNameTypeAhead = '';
  reportForm.form.issue_to[index].email = '';
};

// Methods
const addIssueTo = () => {
  reportForm.form.issue_to.push({
    email: '',
    user: undefined,
    userNameTypeAhead: '',
  });
};

const removeIssueTo = (index: number) => {
  reportForm.form.issue_to.splice(index, 1);
};

const revertSections = () => {
  reportForm.form.report_section_configs = structuredClone(report.value.report_section_configs);
};

const addReportSection = () => {
  reportForm.form.report_section_configs.push({
    report_section_id: undefined,
  });
};

const removeReportSection = (index: number) => {
  reportForm.form.report_section_configs.splice(index, 1);
};

const updateSectionOrder = (event: any) => {
  // Update report_section_configs array with new order
};

const saveReport = () => {
  reportForm.resetErrors();

  if(props.reportId) {
    updateReportAction.request(props.reportId, reportForm.form).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Report Updated',
        text: 'The report has been updated.',
      });
    }).catch((error) => {
      if(error?.status === 422) {
        reportForm.setErrors(error.data);
      }
    });
  } else {
    saveReportAction.request(props.projectId, reportForm.form).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Report Created',
        text: 'The report has been created.',
      });

      router.push({ name: 'ReportsIndex' });
    }).catch((error) => {
      if(error?.status === 422) {
        reportForm.setErrors(error.data);
      }
    });
  }
};
</script>

<style scoped></style>
